import type { IPublicClientApplication } from "@azure/msal-browser";
import type { Edge, Node } from "@xyflow/react";
import type { AssetProfile } from "./services/assets/assetProfiles";

export enum DocumentViewMode {
    TABLE = "table",
    EXPLODE = "explode",
}

export enum NodeKind {
    BUCKET = "bucket",
}

export enum NodeRole {
    ROOT = "root",
    GROUP = "group",
    TITLE = "title",
}

export interface BucketSelection {
    field: string;
    value: string;
}

export interface SelectableField {
    value: string;
    label: string;
}

export interface CountBucket {
    value: string;
    count: number;
}

export interface FacetValue {
    key: string;
    doc_count: number;
}

export interface FacetField {
    field: string;
    facets: FacetValue[];
}

export interface FacetFilter {
    key: string;
    value: string[];
}

export interface FetchFacetsParams {
    instance: IPublicClientApplication;
    index: string;
    filters: FacetFilter[];
    maxResult?: number;
}

export interface Cluster {
    field: string;
    label: string;
    buckets: CountBucket[];
    distinctCount: number;
}

export interface OverlayTarget {
    field: string;
    label: string;
}

export interface OverlayState extends OverlayTarget {
    page: number;
}

export interface OverlayData extends OverlayState {
    buckets: CountBucket[];
}

export interface FieldOption {
    field: string;
    label: string;
    count: number;
}

export interface BuildFacetGraphParams {
    categories: CountBucket[];
    categoryField: string;
    categoryLabel: string;
    rootLabel: string;
    gridPage?: number;
    selected?: BucketSelection;
    clusters: Cluster[];
    subLoading?: boolean;
    overlay?: OverlayData;
    fieldOptions?: FieldOption[];
}

export interface SubFacetsQueryConfig {
    instance: IPublicClientApplication;
    index: string;
    locks: BucketSelection[];
    extraFilters?: FacetFilter[];
}

export type DateRangeValue = [Date | null, Date | null];

export interface UseOverviewGraphParams {
    index: string;
    rootLabel: string;
    category: string;
    categoryLabel: string;
    fields: SelectableField[];
    gridPage?: number;
    selected?: BucketSelection;
    overlay?: OverlayState;
    dateField?: string;
    dateRange?: DateRangeValue;
    assetMode?: boolean;
    assetProfile?: AssetProfile;
    focusedAssetId?: string;
}

export type ExplodeNodeData = {
    label: string;
    role: NodeRole;
    selection: BucketSelection[];
    count?: number;
    dimmed?: boolean;
    focused?: boolean;
    caption?: string;
    title?: boolean;
    skeleton?: boolean;
    muted?: boolean;
    overlay?: OverlayTarget;
    overlayPage?: number;
    gridPage?: number;
    categoryField?: string;
    assetId?: string;
    homeUrl?: string;
    reportTarget?: ReportTarget;
};

export type ReportTarget = {
    datasourceId: string;
    filterKey: string;
    filterValue: string;
    label: string;
};

export interface ExplodeFlow {
    nodes: Node<ExplodeNodeData>[];
    edges: Edge[];
}

export interface ExpandGraphProps {
    index: string;
    rootLabel: string;
    category: string;
    categoryLabel: string;
    fields: SelectableField[];
    selected?: BucketSelection;
    overlay?: OverlayState;
    gridPage: number;
    dateField?: string;
    dateRange?: DateRangeValue;
    onGridPage: (page: number) => void;
    onSelect: (selection: BucketSelection) => void;
    onClearSelection: () => void;
    onSelectCategory: (field: string) => void;
    onClearCategory: () => void;
    onOpen: (selection: BucketSelection[]) => void;
    onOpenOverlay: (overlay: OverlayState) => void;
    onCloseOverlay: () => void;
    assetMode?: boolean;
    assetProfile?: AssetProfile;
    focusedAssetId?: string;
    onFocusAsset?: (assetId: string | undefined) => void;
    onOpenReport?: (target: ReportTarget) => void;
}

export interface DocumentViewsProps {
    index: string;
    namespace?: string;
    datasourceId?: string;
    hasFacets?: boolean;
    assetMode?: boolean;
    rootLabel: string;
    datasourceLabel: string;
    fields: SelectableField[];
    dateField?: string;
    dateFieldLabel?: string;
    onFilteredCountChange?: (count: number | undefined) => void;
}
